import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Box, TextField, Button } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { formInputSx } from '../styles/inputStyles';
import './ManageConnectionsModal.css';

/* Pre-OAuth connect inputs (e.g. a Zendesk subdomain) collected in a styled modal —
   replaces the raw browser prompt. Matches the ManageConnectionsModal shell so it
   looks like every other modal. onSubmit(values) gets the cleaned param map. */
const ConnectParamsModal = ({ open, provider, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const params = (provider && provider.connect_params) || [];
  const [values, setValues] = useState({});

  useEffect(() => { if (open) setValues({}); }, [open, provider]);

  const clean = (key, raw) => {
    let v = (raw || '').trim();
    // A subdomain pasted as a full URL/domain → reduce to the bare subdomain.
    if (key === 'subdomain') v = v.replace(/^https?:\/\//i, '').split(/[./]/)[0].trim();
    return v;
  };

  const missingRequired = params.some((cp) => cp.required && !clean(cp.key, values[cp.key]));

  const submit = () => {
    if (missingRequired) return;
    const out = {};
    params.forEach((cp) => { const v = clean(cp.key, values[cp.key]); if (v) out[cp.key] = v; });
    onSubmit(out);
  };

  const label = provider ? provider.label : t('connect_params_fallback_app');

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          bgcolor: 'white', borderRadius: '10px', boxShadow: 24, width: '90%', maxWidth: 460,
          maxHeight: '90vh', display: 'flex', flexDirection: 'column', outline: 'none',
        }}
      >
        <Box sx={{ flexShrink: 0, px: 2.75, pt: 2.75, pb: 2, borderBottom: '1px solid #f0f0f0' }}>
          <h3 className="manage-connections-modal-title" style={{ marginBottom: 0 }}>{t('connect_params_title', { label })}</h3>
        </Box>

        <Box sx={{ px: 2.75, pt: 2, pb: 2.75, overflowY: 'auto' }}>
          <div className="connection-info-box">
            <FontAwesomeIcon className="connection-info-icon" icon={faCircleInfo} />
            <div className="connection-info-content">
              <p>{t('connect_params_info', { label })}</p>
            </div>
          </div>

          {params.map((cp) => (
            <TextField
              key={cp.key}
              fullWidth
              variant="outlined"
              label={cp.label || cp.key}
              helperText={cp.hint || ''}
              value={values[cp.key] || ''}
              onChange={(e) => setValues((v) => ({ ...v, [cp.key]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              sx={formInputSx}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          ))}

          <div className="modal-actions">
            <Button
              onClick={submit}
              disabled={missingRequired}
              variant="contained"
              sx={{ backgroundColor: '#5616ea', '&:hover': { backgroundColor: '#4a14d6' } }}
            >
              {t('connect_params_title', { label })}
            </Button>
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{ color: '#5616ea', borderColor: '#5616ea', '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' } }}
            >
              {t('cancel')}
            </Button>
          </div>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConnectParamsModal;
